import React, { useEffect } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { TableVirtuoso } from 'react-virtuoso';
import { Checkbox, Spin, Tooltip, Icon } from 'antd';
import styled from 'styled-components';

import { WhatsappIcon, phoneMask } from '~/icons/WhatsappIcon';
import { getSubdomain } from '~/helpers/getSubdomain';

import { loadPartnersReport } from '~/screens/Reports/actions';

var frontBaseUrl = 'http://app.' + process.env.REACT_APP_DOMAIN;

var WhatsappLink = styled.a`
  > img {
    width: 20px;
    height: 20px;
    margin-left: 5px;
  }
`;

var COL = { check: 50, name: 'auto', partner: 'auto', phone: 200 };

var thBase = {
    padding: '8px 12px', background: '#fafafa',
    borderTop: '1px solid #e8e8e8', borderBottom: '1px solid #e8e8e8',
    borderRight: '1px solid #e8e8e8',
    fontWeight: 500, textAlign: 'left', whiteSpace: 'nowrap',
    overflow: 'hidden', textOverflow: 'ellipsis'
};
var tdBase = {
    padding: '8px 12px', background: '#fff',
    borderBottom: '1px solid #e8e8e8', borderRight: '1px solid #e8e8e8',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
};

function thS(w, extra) {
    var s = Object.assign({}, thBase, extra);
    if (w !== 'auto') s.width = w;
    return s;
}
function tdS(w, extra) {
    var s = Object.assign({}, tdBase, extra);
    if (w !== 'auto') s.width = w;
    return s;
}

var Audience = React.memo(function Audience(props) {
    var customersChecked = props.customersChecked;
    var setCustomersChecked = props.setCustomersChecked;
    var data = props.data;

    var dispatch = useDispatch();
    var partnersData = useSelector(function(state) { return state.reports.partners.data; });
    var isLoading = useSelector(function(state) { return state.reports.partners.isLoading; });

    useEffect(function() {
        if (!partnersData || partnersData.length === 0) {
            dispatch(loadPartnersReport());
        }
    }, []);

    var sortState = React.useState('customer_name');
    var sortField = sortState[0]; var setSortField = sortState[1];
    var orderState = React.useState('ascend');
    var sortOrder = orderState[0]; var setSortOrder = orderState[1];

    var processedData = React.useMemo(function() {
        var src = partnersData || [];
        if (sortField) {
            src = src.slice().sort(function(a, b) {
                return ((a[sortField] || '') + '').localeCompare((b[sortField] || '') + '');
            });
            if (sortOrder === 'descend') src.reverse();
        }
        return src;
    }, [partnersData, sortField, sortOrder]);

    function toggleSort(field) {
        if (sortField === field) {
            if (sortOrder === 'ascend') { setSortOrder('descend'); }
            else { setSortField(null); setSortOrder(null); }
        } else {
            setSortField(field); setSortOrder('ascend');
        }
    }

    function sortIcon(field) {
        if (sortField !== field) return React.createElement(Icon, { type: 'caret-down', style: { marginLeft: 4, fontSize: 10, opacity: 0.3 } });
        return React.createElement(Icon, {
            type: sortOrder === 'ascend' ? 'caret-up' : 'caret-down',
            style: { marginLeft: 4, fontSize: 10, color: '#1890ff' }
        });
    }

    var selectedKeysSet = React.useMemo(function() { return new Set(customersChecked); }, [customersChecked]);
    var allChecked = processedData.length > 0 && customersChecked.length === processedData.length;
    var someChecked = customersChecked.length > 0 && !allChecked;

    function onToggleAll(e) {
        if (e.target.checked) {
            setCustomersChecked(processedData.map(function(r) { return r.id; }));
        } else {
            setCustomersChecked([]);
        }
    }

    function onToggleRow(id) {
        var idx = customersChecked.indexOf(id);
        if (idx === -1) { setCustomersChecked(customersChecked.concat([id])); }
        else { setCustomersChecked(customersChecked.filter(function(k) { return k !== id; })); }
    }

    var getWhatsappMessage = function(partnerName) {
        var msg = 'Olá, ' + partnerName + '! Tudo bem? \n\n' + (data.name || '') + '\n\n\n' + (data.description || '') + '\n\n\nClique no link abaixo para indicar: ' + frontBaseUrl + '/i/' + getSubdomain() + '/' + (data.token || '') + '?c=';
        return encodeURIComponent(msg);
    };

    var tableComponents = React.useMemo(function() {
        return {
            Table: function(p) {
                return React.createElement('table', Object.assign({}, p, {
                    style: Object.assign({}, p.style, {
                        borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed', width: '100%'
                    })
                }));
            },
            TableRow: function(p) {
                return React.createElement('tr', Object.assign({}, p, {
                    className: 'row-clabs' + (p.className ? ' ' + p.className : '')
                }));
            }
        };
    }, []);

    var fixedHeaderContent = React.useCallback(function() {
        return (
            <tr>
                <th style={thS(COL.check, { textAlign: 'center', borderLeft: '1px solid #e8e8e8' })}>
                    <Checkbox checked={allChecked} indeterminate={someChecked} onChange={onToggleAll} />
                </th>
                <th style={thS(COL.name, { cursor: 'pointer' })} onClick={function() { toggleSort('customer_name'); }}>
                    Empresa {sortIcon('customer_name')}
                </th>
                <th style={thS(COL.partner, { cursor: 'pointer' })} onClick={function() { toggleSort('partner_name'); }}>
                    Sócio {sortIcon('partner_name')}
                </th>
                <th style={thS(COL.phone)}>Whatsapp</th>
            </tr>
        );
    }, [allChecked, someChecked, sortField, sortOrder]);

    function itemContent(index, record) {
        var isSelected = selectedKeysSet.has(record.id);
        var cleanPhone = record.partner_phone ? record.partner_phone.replace(/\D/g, '') : '';

        return (
            <>
                <td style={tdS(COL.check, { textAlign: 'center', borderLeft: '1px solid #e8e8e8' })}>
                    <Checkbox checked={isSelected} onChange={function() { onToggleRow(record.id); }} />
                </td>
                <td style={tdS(COL.name)}>{record.customer_name}</td>
                <td style={tdS(COL.partner)}>{record.partner_name}</td>
                <td style={tdS(COL.phone)}>
                    {cleanPhone ? (
                        <Tooltip title="Enviar convite para o Whatsapp">
                            <WhatsappLink
                                onClick={function() {
                                    if (!isSelected) { onToggleRow(record.id); }
                                }}
                                href={'https://api.whatsapp.com/send?phone=55' + cleanPhone + '&text=' + getWhatsappMessage(record.partner_name)}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <WhatsappIcon /> {phoneMask(cleanPhone)}
                            </WhatsappLink>
                        </Tooltip>
                    ) : '-'}
                </td>
            </>
        );
    }

    return (
        <Spin spinning={isLoading}>
            <TableVirtuoso
                style={{ height: 'calc(100vh - 200px)', border: '1px solid #e8e8e8' }}
                data={processedData}
                components={tableComponents}
                fixedHeaderContent={fixedHeaderContent}
                itemContent={itemContent}
                overscan={20}
            />
            <div style={{ padding: '8px 16px', background: '#fafafa', border: '1px solid #e8e8e8', borderTop: 'none', fontSize: 13 }}>
                {customersChecked.length > 0 && (
                    <span style={{ marginRight: 12 }}>
                        <strong>{customersChecked.length}</strong> selecionado(s)
                    </span>
                )}
                Total: {processedData.length} sócio(s)
            </div>
        </Spin>
    );
});

export default Audience;
